import type { ReportFinding, ReportKeyValue, ReportLimitation, ReportModel } from './assembly';
import type { ReportVariant } from '../domain/report';
import { DEFECT_SEVERITY_COLORS } from '../domain/severity';
import { flowHardWrappedText } from './flowText';
import { demoWatermarkStyle } from './watermark';

// 2026-10-19 (Owner item 16): a defect card's left bar and its tier chip carry the tier's fixed colour.
function severityStyle(f: Pick<ReportFinding, 'severity'>): string {
  return f.severity ? ` style="border-left-color:${DEFECT_SEVERITY_COLORS[f.severity]}" data-severity="${f.severity}"` : '';
}

function severityChip(severity: ReportFinding['severity'], label: string): string {
  if (!severity) return '';
  const color = DEFECT_SEVERITY_COLORS[severity];
  return `<span class="rp-sev" style="color:${color};border-color:${color}" data-severity="${severity}">${esc(label)}</span>`;
}

// One HTML rendering of the report model, used for the in-app preview and as the PDF source
// (device-side rasterization by src/report/pdf.ts). Print-first styling: a single column, black
// text on white, the same section order as the model (Final Authority §20.1), photos at 4:3 with
// their figure captions. No app chrome, no AI / app / model-vendor disclaimers (OC §8.10).
function esc(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function paragraphs(text: string): string {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => `<p class="rp-p">${esc(line)}</p>`)
    .join('');
}

// The scope of the inspection is the approved agreement text, hard-wrapped at the width of its
// plain-text source. On paper its sentences flow to the page and its bullets hang
// (./flowText.ts — the words are untouched; 2026-09-21).
function flowedParagraphs(text: string): string {
  return flowHardWrappedText(text)
    .map((block) => `<p class="rp-p${block.kind === 'bullet' ? ' rp-bullet' : ''}">${esc(block.text)}</p>`)
    .join('');
}

function kvTable(rows: ReportKeyValue[]): string {
  return `<table class="rp-kv">${rows.map((r) => `<tr><th>${esc(r.key)}</th><td>${esc(r.value)}</td></tr>`).join('')}</table>`;
}

// One limitation entry. NY F1 Round 3: a limited portion names the portion (OD-5 = A) and, when no note
// was typed, prints the frozen NY wording that states the reasons why (OD-7 = C); a typed note prints as
// the note, exactly as in Round 2. `titlePrefix` is already escaped.
function limitationLine(L: ReportModel['labels'], l: ReportLimitation, titlePrefix: string): string {
  const portion = l.partial ? ` · ${esc(L.portion)}: ${esc(l.portion.length > 0 ? l.portion : L.portionUnspecified)}` : '';
  const reasonText = l.reasonText ? ` · ${esc(l.reasonText)}` : '';
  const note = l.note ? ` · ${esc(L.note)}: ${esc(l.note)}` : '';
  return `<div class="rp-lim${l.partial ? ' rp-lim-partial' : ''}"><strong>${titlePrefix}${esc(l.itemTitle)}</strong> — ${esc(L.reason)}: ${esc(l.reason)}${portion}${reasonText}${note}</div>`;
}

export const REPORT_CSS = `
.rp{font-family:Inter,'PingFang SC','Hiragino Sans GB','Microsoft YaHei',system-ui,sans-serif;color:#0b1c30;background-color:#fff;font-size:11.5pt;line-height:1.5;}
.rp *{box-sizing:border-box}
.rp h1{font-size:22pt;line-height:1.2;margin:0 0 4pt;letter-spacing:-0.01em}
.rp .rp-sub{font-size:12pt;color:#404943;margin:0 0 2pt}
.rp .rp-variant{display:inline-block;margin-top:6pt;padding:2pt 8pt;border:1px solid #bfc9c1;border-radius:999px;font-size:9.5pt;color:#404943}
.rp .rp-cover{margin:10pt 0 4pt;page-break-inside:avoid}
.rp .rp-cover img{display:block;width:100%;aspect-ratio:3/2;object-fit:cover;border:1px solid #e2e8f0;border-radius:8pt}
.rp h2{font-size:14pt;margin:18pt 0 6pt;padding-bottom:3pt;border-bottom:1.5px solid #0f5238;color:#0f5238;letter-spacing:0.02em;text-transform:uppercase}
.rp h3{font-size:12.5pt;margin:12pt 0 2pt}
.rp .rp-section-sub{color:#404943;font-size:10pt;margin:0 0 6pt}
.rp .rp-kv{border-collapse:collapse;width:100%;margin:4pt 0 2pt}
.rp .rp-kv th{text-align:left;font-weight:600;color:#404943;width:32%;padding:3pt 6pt 3pt 0;vertical-align:top;border-bottom:1px solid #e2e8f0}
.rp .rp-kv td{padding:3pt 0;vertical-align:top;border-bottom:1px solid #e2e8f0}
.rp .rp-p{margin:0 0 4pt}
.rp .rp-summary{display:flex;gap:8pt;flex-wrap:wrap;margin:6pt 0}
.rp .rp-stat{flex:1 1 120pt;border:1px solid #e2e8f0;border-radius:8pt;padding:6pt 8pt;font-size:10.5pt}
.rp .rp-finding{border-left:3pt solid #ba1a1a;padding:4pt 0 4pt 10pt;margin:8pt 0 10pt}
.rp .rp-finding h4{margin:0 0 3pt;font-size:12pt}
.rp .rp-meta{font-size:9.5pt;color:#404943;margin:0 0 4pt;text-transform:uppercase;letter-spacing:0.04em}
.rp .rp-label{font-weight:600;font-size:9.5pt;color:#404943;text-transform:uppercase;letter-spacing:0.05em;margin:6pt 0 1pt}
.rp .rp-photos{display:flex;flex-wrap:wrap;gap:8pt;margin:4pt 0 2pt}
.rp .rp-photo{width:calc(50% - 4pt);page-break-inside:avoid}
.rp .rp-photo img{display:block;width:100%;aspect-ratio:4/3;object-fit:cover;border:1px solid #e2e8f0;border-radius:6pt}
.rp .rp-photo .rp-cap{font-size:9.5pt;font-style:italic;color:#404943;margin:2pt 0 0}
.rp .rp-lim{margin:0 0 4pt;padding:4pt 8pt;background:rgba(232,236,255,0.35);border-radius:6pt;font-size:10.5pt}
.rp .rp-results{font-size:10pt;color:#404943;margin:0 0 4pt}
.rp .rp-note{font-size:10.5pt;color:#404943;margin:0 0 6pt}
.rp .rp-terms{font-size:9.5pt;color:#404943}
.rp .rp-bullet{padding-left:10pt;text-indent:-10pt}
.rp .rp-foot{margin-top:16pt;font-size:9pt;color:#707973;border-top:1px solid #e2e8f0;padding-top:4pt}
.rp .rp-muted{color:#707973}
.rp .rp-demo-banner{border:2px solid #ba1a1a;color:#ba1a1a;font-weight:700;text-align:center;padding:6pt;margin:0 0 10pt;letter-spacing:0.08em;text-transform:uppercase}
.rp .rp-description .rp-kv{margin:2pt 0 4pt}
.rp .rp-description .rp-kv th,.rp .rp-description .rp-kv td{font-size:10pt;padding:2pt 6pt 2pt 0}
.rp .rp-obs-grid{display:grid;grid-template-columns:minmax(0,1fr);row-gap:2pt;font-size:9.5pt;margin:2pt 0 6pt}
.rp .rp-obs-row{min-width:0;page-break-inside:avoid}
.rp .rp-obs-line{display:grid;grid-template-columns:fit-content(70%) minmax(6pt,1fr) fit-content(48%);column-gap:4pt;align-items:baseline;align-items:last baseline;min-width:0}
.rp .rp-obs-sentence{margin:0 0 4pt;padding-left:8pt;font-size:9pt;color:#334155}
.rp .rp-sys{margin:2pt 0;font-size:10pt}
.rp .rp-obs-title{min-width:0;overflow-wrap:break-word}
.rp .rp-obs-dots{min-width:6pt;border-bottom:1px dotted #bfc9c1;transform:translateY(-3pt)}
.rp .rp-obs-state{min-width:0;text-align:right;overflow-wrap:break-word}
.rp .rp-obs-tags{margin:0 0 1pt;padding-left:8pt;font-size:8.5pt;line-height:1.35;color:#707973;overflow-wrap:break-word}
.rp .rp-obs-defect .rp-obs-state{color:#ba1a1a;font-weight:600}
.rp .rp-obs-pointer .rp-obs-state,.rp .rp-obs-unrecorded .rp-obs-state{color:#707973}
.rp .rp-obs-tag-partial{color:#404943;font-weight:600}
@container report-preview (max-width:560px){.rp h1{font-size:19pt}.rp .rp-kv th{width:38%}}
.rp .rp-lim-partial{border-left:2pt solid #bfc9c1}
.rp .rp-notpresent{margin:2pt 0 6pt}
.rp .rp-np{margin:0 0 2pt;font-size:9.5pt;color:#404943}
.rp .rp-voluntary{margin:8pt 0 4pt}
.rp .rp-vol{border-left:3pt solid #bfc9c1;padding:4pt 0 4pt 10pt;margin:6pt 0 8pt}
.rp .rp-vol h4{margin:0 0 3pt;font-size:12pt}
.rp .rp-sev{display:inline-block;margin:0 6pt 0 0;padding:0 6pt;border:1.2px solid;border-radius:999px;font-size:8.5pt;font-weight:700;letter-spacing:0.04em;text-transform:none}
.rp .rp-dsum{display:flex;flex-direction:column;gap:5pt;margin:4pt 0 8pt}
.rp .rp-dsum-line{display:flex;align-items:center;gap:8pt;border-left:3pt solid #ba1a1a;padding:3pt 0 3pt 8pt;page-break-inside:avoid}
.rp .rp-dsum-text{flex:1 1 auto;min-width:0}
.rp .rp-dsum-title{margin:2pt 0 0;font-weight:600;font-size:10.5pt}
.rp .rp-dsum-meta{margin:0;font-size:9pt;color:#404943}
.rp .rp-dsum-photo{flex:0 0 auto;width:62pt;height:46pt;object-fit:cover;border:1px solid #e2e8f0;border-radius:4pt}
.rp .rp-dsum-note{font-weight:600}
.rp .rp-unable-area{margin:0 0 4pt;font-size:10pt}
.rp .rp-governed{margin-top:6pt;font-weight:600}
.rp .rp-brand{display:flex;align-items:center;gap:10pt;margin:0 0 12pt;padding:0 0 8pt;border-bottom:1px solid #e2e8f0}
.rp .rp-brand-logo{max-width:120pt;max-height:48pt;object-fit:contain}
.rp .rp-brand-text{min-width:0}
.rp .rp-brand-name{margin:0;font-size:13pt;font-weight:700}
.rp .rp-brand-contact{margin:1pt 0 0;font-size:9.5pt;color:#404943}
.rp .rp-inspector{display:flex;align-items:flex-start;gap:10pt}
.rp .rp-inspector .rp-kv{flex:1 1 auto}
.rp .rp-more-photos{margin:2pt 0 0;font-size:9pt;color:#404943;font-style:italic}
.rp .rp-inspector-photo{flex:0 0 auto;width:56pt;height:56pt;border-radius:50%;object-fit:cover;border:1px solid #e2e8f0;margin-top:4pt}
`;

// 2026-10-19 (Owner item 21): each limitation is stated in full, with its reason, in its own area
// (197-5.3(c)). The closing section no longer repeats every entry — it lists, per area, the items not
// inspected and their reason, one compact paragraph per area.
function unableHtml(model: ReportModel): string {
  const L = model.labels;
  const unableByArea = new Map<string, ReportModel['unable']>();
  for (const u of model.unable) unableByArea.set(u.areaName, [...(unableByArea.get(u.areaName) ?? []), u]);
  return model.unable.length === 0
    ? `<p class="rp-note rp-muted">—</p>`
    : `<p class="rp-note">${esc(L.unableSeeArea)}</p>${[...unableByArea.entries()]
        .map(
          ([areaName, entries]) =>
            `<p class="rp-unable-area"><strong>${esc(areaName)}</strong> — ${entries
              .map((u) => `${esc(u.itemTitle)}${u.partial ? ` (${esc(L.portion)}: ${esc(u.portion.length > 0 ? u.portion : L.portionUnspecified)})` : ''}: ${esc(u.reason)}${u.note ? ` · ${esc(u.note)}` : ''}`)
              .join('; ')}</p>`
        )
        .join('')}`;
}

// 2026-10-19 (Owner item 15 = D-17 ①): the Summary of Deficiencies, red first, one line per defect.
function defectSummaryHtml(model: ReportModel): string {
  const L = model.labels;
  const lines =
    model.defectSummary.length === 0
      ? `<p class="rp-note rp-muted">${esc(L.defectSummaryEmpty)}</p>`
      : `<div class="rp-dsum">${model.defectSummary
          .map(
            (line) => `<div class="rp-dsum-line" data-severity="${line.severity}" style="border-left-color:${DEFECT_SEVERITY_COLORS[line.severity]}">
  <div class="rp-dsum-text">${severityChip(line.severity, line.severityLabel)}<p class="rp-dsum-title">${esc(line.title)}</p><p class="rp-dsum-meta">${esc([line.areaName, line.trade ? `${L.responsibleTrade}: ${line.trade}` : ''].filter((s) => s.length > 0).join(' · '))}</p></div>
  ${line.photo ? `<img class="rp-dsum-photo" src="${line.photo}" alt="">` : ''}
</div>`
          )
          .join('')}</div>`;
  return `<section class="rp-defect-summary" data-defect-summary="true"><h2>${esc(L.defectSummary)}</h2><p class="rp-note rp-dsum-note">${esc(L.defectSummaryNote)}</p>${lines}</section>`;
}

// The report root. A Demo report carries its watermark as the root's background: under every line of
// text, never a blank block of its own that a screenshot can leave out (Owner 2026-09-12;
// ./watermark.ts). The running header and footer (Owner item 18) travel as data attributes the PDF
// renderer prints in every page margin (src/report/pdf.ts). The English client report gets an
// invisible text layer in its PDF (searchable, selectable); a Chinese document does not — the PDF
// engine carries no CJK font.
function rootOpen(model: ReportModel, extraClass: string): string {
  const L = model.labels;
  const watermark = model.demo ? ` style="${esc(demoWatermarkStyle(L.demoWatermark))}"` : '';
  const b = model.branding;
  const running = b ? ` data-running-header="${esc(b.runningHeader)}" data-running-footer="${esc(b.runningFooter)}" data-page-label="${esc(L.page)}"` : '';
  return `<div class="rp${extraClass ? ` ${extraClass}` : ''}"${watermark} data-demo="${model.demo ? 'true' : 'false'}" data-text-layer="${model.locale === 'en-US' ? 'true' : 'false'}"${running}>`;
}

// 2026-10-19 (Owner item 18): the inspector's company block on the cover.
function brandBlockHtml(model: ReportModel): string {
  const b = model.branding;
  return b && (b.companyName.length > 0 || b.contact.length > 0 || b.logo)
    ? `<header class="rp-brand" data-brand="true">${b.logo ? `<img class="rp-brand-logo" src="${b.logo}" alt="">` : ''}<div class="rp-brand-text">${b.companyName ? `<p class="rp-brand-name">${esc(b.companyName)}</p>` : ''}${b.contact ? `<p class="rp-brand-contact">${esc(b.contact)}</p>` : ''}</div></header>`
    : '';
}

export function renderReportBody(model: ReportModel): string {
  const L = model.labels;
  const sections = model.sections
    .map((section) => {
      const findings =
        section.findings.length === 0
          ? `<p class="rp-note rp-muted">${esc(L.noFindings)}</p>`
          : section.findings
              .map(
                // The tier replaces the old 1–5 priority on the card: one scale, applied to every defect.
                (f) => `<div class="rp-finding"${severityStyle(f)}>
  <p class="rp-meta">${severityChip(f.severity, f.severityLabel)}${[f.itemTitle ? esc(f.itemTitle) : '', f.trade ? esc(L.responsibleTrade) + ': ' + esc(f.trade) : ''].filter((part) => part.length > 0).join(' · ')}</p>
  <h4>${esc(f.title)}</h4>
  <p class="rp-label">${esc(L.observation)}</p>${paragraphs(f.observation)}
  ${f.recommendation ? `<p class="rp-label">${esc(L.recommendation)}</p>${paragraphs(f.recommendation)}` : ''}
  ${
    f.photos.length > 0
      ? `<p class="rp-label">${esc(L.evidence)}</p><div class="rp-photos">${f.photos
          .map((ph) => `<div class="rp-photo">${ph.dataUrl ? `<img src="${ph.dataUrl}" alt="">` : ''}<p class="rp-cap">${esc(ph.figure)}${ph.caption ? ': ' + esc(ph.caption) : ''}</p></div>`)
          .join('')}</div>${f.morePhotosNote ? `<p class="rp-more-photos">${esc(f.morePhotosNote)}</p>` : ''}`
      : ''
  }
</div>`
              )
              .join('');
      const limitations = section.limitations.map((l) => limitationLine(L, l, '')).join('');
      // NY F1 Round 2 · Final Authority §20.1 (deviation 2026-09-06): counts line → 描述信息 →
      // OBSERVED COMPONENTS (limitation rows appear as a "Not observed →" pointer to their limitation
      // entry, never duplicated inline) → NOT PRESENT (only when the area has such rows; D-17 ②) →
      // findings → limitations.
      // The component list is ONE full-width column since 2026-09-21. It was a two-column dense grid
      // until every row got its report sentence (2026-09-14): a sentence spans the page, so each component
      // took a grid row of its own and sat in the LEFT half only — its state in the middle of the page,
      // the right half blank, long names folded for nothing (seen on the real PDF, Owner 2026-09-21).
      const description = section.descriptors.length > 0 ? `<div class="rp-description"><p class="rp-label">${esc(L.description)}</p>${kvTable(section.descriptors)}</div>` : '';
      // One component = one line (name · leader · SHORT state) and, under it, whatever else is said about
      // the row — the "part not observed →" pointer, the supplemental-observation tag — on a line of its
      // own that may wrap. Owner device test 2026-09-21: the state, the pointer and the tag used to be
      // ONE unbreakable string beside the name; in a narrow column it took the whole line and the name
      // was laid out one character per line, overlapping the next column. Neither column can now be
      // starved: both tracks are fit-content with a cap, and both texts may wrap.
      const obsRow = (title: string, state: string, cls: string, tags: ReadonlyArray<{ text: string; partial?: boolean }> = [], sentence = '') =>
        `<div class="rp-obs-row ${cls}"><div class="rp-obs-line"><span class="rp-obs-title">${esc(title)}</span><span class="rp-obs-dots"></span><span class="rp-obs-state">${esc(state)}</span></div>${
          // The labels were written as suffixes ("… · part not observed →"); on a line of their own the first one opens it.
          tags.length > 0 ? `<div class="rp-obs-tags">${tags.map((tag, index) => `<span class="rp-obs-tag${tag.partial ? ' rp-obs-tag-partial' : ''}">${esc(index === 0 ? tag.text.charAt(0).toUpperCase() + tag.text.slice(1) : tag.text)}</span>`).join(' · ')}</div>` : ''
        }</div>${sentence ? `<p class="rp-obs-sentence">${esc(sentence)}</p>` : ''}`;
      const observed =
        section.observed.length + section.limitations.length > 0
          ? `<div class="rp-observed"><p class="rp-label">${esc(L.observedComponents)}</p><div class="rp-obs-grid">${section.observed
              .map((o) =>
                obsRow(
                  o.itemTitle,
                  o.stateLabel,
                  `${o.state === 'observedDefect' ? 'rp-obs-defect' : o.state === 'notRecorded' ? 'rp-obs-unrecorded' : ''}${o.partial ? ' rp-obs-partial' : ''}`,
                  [...(o.partialLabel.length > 0 ? [{ text: o.partialLabel, partial: true }] : []), ...(o.supplemental ? [{ text: model.supplementalLabel }] : [])],
                  o.sentence
                )
              )
              .join('')}${section.limitations
              .filter((l) => !l.partial)
              // 2026-10-28 (iPhone run 10-26 #10: the boiler relief valve printed only "Not observed →"): the
              // pointer says why on its own line; the full entry with its NY wording follows in this section.
              .map((l) => obsRow(l.itemTitle, model.notObservedLabel, 'rp-obs-pointer', [{ text: `${L.reason}: ${l.reason}` }]))
              .join('')}</div></div>`
          : '';
      const notPresent =
        section.notPresent.length > 0
          ? `<div class="rp-notpresent"><p class="rp-label">${esc(L.notPresent)}</p>${section.notPresent.map((n) => `<p class="rp-np"><strong>${esc(n.itemTitle)}</strong> — ${esc(n.wording)}${n.note ? ` · ${esc(n.note)}` : ''}</p>`).join('')}</div>`
          : '';
      // NY F1 Round 5 (OD-13 / agreement v2 §3.5): voluntary observations print in their OWN block,
      // each carrying the frozen "not required by the New York Standards of Practice" tag. They are
      // not findings of this inspection and are not observed components.
      const otherObservations =
        section.otherObservations.length > 0
          ? `<div class="rp-voluntary" data-voluntary="true"><p class="rp-label">${esc(L.otherObservations)}</p>${section.otherObservations
              .map(
                (o) => `<div class="rp-vol">
  <p class="rp-meta">${esc(L.voluntaryTag)}</p>
  <h4>${esc(o.title)}</h4>${paragraphs(o.observation)}${o.recommendation ? `<p class="rp-label">${esc(L.recommendation)}</p>${paragraphs(o.recommendation)}` : ''}
  ${
    o.photos.length > 0
      ? `<div class="rp-photos">${o.photos
          .map((ph) => `<div class="rp-photo">${ph.dataUrl ? `<img src="${ph.dataUrl}" alt="">` : ''}<p class="rp-cap">${esc(ph.figure)}${ph.caption ? ': ' + esc(ph.caption) : ''}</p></div>`)
          .join('')}</div>${o.morePhotosNote ? `<p class="rp-more-photos">${esc(o.morePhotosNote)}</p>` : ''}`
      : ''
  }
</div>`
              )
              .join('')}</div>`
          : '';
      return `<section class="rp-area" data-area="${esc(section.areaId)}">
  <h3>${esc(section.title)}</h3>
  <p class="rp-section-sub">${esc(section.subtitle)}</p>
  <p class="rp-results">${esc(section.results)}</p>
  ${section.note ? `<p class="rp-label">${esc(L.areaNote)}</p>${paragraphs(section.note)}` : ''}
  ${description}
  ${observed}
  ${notPresent}
  <div class="rp-findings">${findings}</div>
  ${otherObservations}
  ${limitations}
</section>`;
    })
    .join('');

  const unable = unableHtml(model);
  const defectSummary = defectSummaryHtml(model);
  const b = model.branding;
  return `${rootOpen(model, '')}
${model.demo ? `<div class="rp-demo-banner">${esc(L.demoBanner)}</div>` : ''}
${brandBlockHtml(model)}
<h1>${esc(model.title)}</h1>
<p class="rp-sub">${esc(model.address)}</p>
${model.variantLabel ? `<span class="rp-variant">${esc(model.variantLabel)}</span>` : ''}
${model.governedNote ? `<p class="rp-note rp-governed">${esc(model.governedNote)}</p>` : ''}
${model.coverPhoto ? `<div class="rp-cover" data-cover="true"><img src="${model.coverPhoto.dataUrl}" alt=""></div>` : ''}
<h2>${esc(L.propertyInformation)}</h2>${kvTable(model.property)}
<h2>${esc(L.clientInformation)}</h2>${kvTable(model.client)}
<h2>${esc(L.inspectionDetails)}</h2>${b?.inspectorPhoto ? `<div class="rp-inspector"><img class="rp-inspector-photo" src="${b.inspectorPhoto}" alt="">${kvTable(model.inspection)}</div>` : kvTable(model.inspection)}
<h2>${esc(L.agreement)}</h2>${kvTable([
    { key: L.agreementDocument, value: model.agreement.document },
    { key: L.fee, value: model.agreement.fee },
    { key: L.agreementSigned, value: model.agreement.signed }
  ])}
<p class="rp-note">${esc(L.agreementSeparate)}</p>
<h2>${esc(L.summary)}</h2>
<div class="rp-summary"><div class="rp-stat">${esc(model.summary.areas)}</div><div class="rp-stat">${esc(model.summary.items)}</div><div class="rp-stat">${esc(model.summary.findings)}</div><div class="rp-stat">${esc(model.summary.limitations)}</div></div>
<p class="rp-note">${esc(model.limitedScopeStatement)}</p>
${defectSummary}
${model.systems.length > 0 ? `<h2>${esc(L.systemsSummary)}</h2>${model.systems.map((s) => `<p class="rp-sys"><strong>${esc(s.title)}:</strong> ${esc(s.sentence)}</p>`).join('')}` : ''}
<h2>${esc(L.sections)}</h2>
${sections}
<h2>${esc(L.unable)}</h2>
${unable}
${model.inspectorNotes.trim().length > 0 ? `<h2>${esc(L.inspectorNotes)}</h2>${paragraphs(model.inspectorNotes)}` : ''}
<h2>${esc(L.terms)}</h2>
<div class="rp-terms">${flowedParagraphs(model.terms)}</div>
<p class="rp-foot">${esc(L.generatedAt)}: ${esc(model.generatedAt)}</p>
</div>`;
}

export function renderReportHtml(model: ReportModel): string {
  return `<style>${REPORT_CSS}</style>${renderReportBody(model)}`;
}

// Owner decision list 2026-10-19, item 23: the client's Chinese summary — its own short PDF for a
// Chinese-reading client or agent. The property, the inspection details, the totals, the Summary of
// Deficiencies (Chinese tier names, area names, item titles and trades; the inspector's own words as
// written) and the areas not inspected. It says at the top that it is for reference only and that the
// English report governs. Nothing about how the report was written.
export function renderChineseSummaryBody(model: ReportModel): string {
  const L = model.labels;
  return `${rootOpen(model, 'rp-client-summary')}
${model.demo ? `<div class="rp-demo-banner">${esc(L.demoBanner)}</div>` : ''}
${brandBlockHtml(model)}
<h1>${esc(model.title)}</h1>
<p class="rp-sub">${esc(model.address)}</p>
${model.variantLabel ? `<span class="rp-variant">${esc(model.variantLabel)}</span>` : ''}
${model.governedNote ? `<p class="rp-note rp-governed" data-governed="true">${esc(model.governedNote)}</p>` : ''}
<h2>${esc(L.inspectionDetails)}</h2>${kvTable(model.inspection)}
<h2>${esc(L.summary)}</h2>
<div class="rp-summary"><div class="rp-stat">${esc(model.summary.areas)}</div><div class="rp-stat">${esc(model.summary.items)}</div><div class="rp-stat">${esc(model.summary.findings)}</div><div class="rp-stat">${esc(model.summary.limitations)}</div></div>
${defectSummaryHtml(model)}
<h2>${esc(L.unable)}</h2>
${unableHtml(model)}
<p class="rp-foot">${esc(L.generatedAt)}: ${esc(model.generatedAt)}</p>
</div>`;
}

// The HTML of one report output: the client's Chinese summary has its own layout, every other report
// variant is the full report.
export function renderVariantHtml(variant: ReportVariant, model: ReportModel): string {
  return variant === 'clientChineseSummary' ? `<style>${REPORT_CSS}</style>${renderChineseSummaryBody(model)}` : renderReportHtml(model);
}
